import React, { useState, useEffect, useRef } from "react";
import {
  Bell, Lock, Trash2, Facebook, Phone, CheckCircle2, AlertCircle, Loader2, Camera, User, Save,
  CreditCard, Crown, Zap, PhoneForwarded, Copy, ArrowRight, Sparkles,
  Building2, ChevronLeft, ChevronRight, Eye, EyeOff, ShieldCheck, Check,
} from "lucide-react";
import WelcomeBanner from "../../components/WelcomeBanner";
import PasswordStrengthMeter from "../../components/PasswordStrengthMeter";
import { useAuth, apiClient, formatApiError } from "../../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import NumberSetup from "./NumberSetup";
import TopUpModal from "./TopUpModal";

const PLAN_META = {
  free: { label: "Free", cls: "bg-slate-100 text-slate-700 border-slate-200", icon: User },
  pro: { label: "Pro", cls: "bg-[#1D6AFF]/10 text-[#1552C6] border-[#1D6AFF]/25", icon: Sparkles },
  elite: { label: "Elite", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Crown },
};
const areaCodeOf = (phone) => { const m = /\+?1?\D*(\d{3})/.exec(phone || ""); return m ? m[1] : ""; };

export default function Settings() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(user?.preferences || { smsAlerts: true, emailDigest: true });
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "", agency: user?.agency || "", avatar: user?.avatar || null });
  const [profileSaved, setProfileSaved] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwBusy, setPwBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [savedToast, setSavedToast] = useState(false);

  // calling & texting
  const [sms, setSms] = useState(null);
  const [showNumberSetup, setShowNumberSetup] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const [copied, setCopied] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  const plan = user?.plan || "free";
  const isPaid = plan === "pro" || plan === "elite";
  const pm = PLAN_META[plan] || PLAN_META.free;

  const loadSms = () => { if (isPaid) apiClient.get("/sms/status").then(({ data }) => setSms(data)).catch(() => {}); };
  useEffect(() => { loadSms(); }, [isPaid]); // eslint-disable-line

  const onAvatar = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => setProfile((p) => ({ ...p, avatar: ev.target.result }));
    r.readAsDataURL(f);
  };
  const saveProfile = (e) => {
    e.preventDefault();
    updateUser(profile);
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 2200);
  };
  const savePrefs = (p) => {
    setPrefs(p);
    updateUser({ preferences: { ...user.preferences, ...p } });
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 1800);
  };
  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await apiClient.post("/billing/portal", { plan_id: "_", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error("Couldn't open billing", { description: formatApiError(e) }); setPortalBusy(false); }
  };
  const copyNumber = async () => {
    try { await navigator.clipboard.writeText(sms?.smart_number_display || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_e) { /* */ }
  };
  const onNumberActivated = (data) => {
    setShowNumberSetup(false);
    setSms((s) => ({ ...(s || {}), active: true, smart_number_display: data.smart_number_display, smart_number: data.smart_number }));
    loadSms();
    toast.success("Your number is live", { description: `${data.smart_number_display} is ready for calls & texts.` });
  };

  const submitPw = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (!pw.current.trim()) return setPwMsg({ type: "error", text: "Enter your current password." });
    if (pw.next.length < 8) return setPwMsg({ type: "error", text: "New password must be at least 8 characters." });
    if (pw.next !== pw.confirm) return setPwMsg({ type: "error", text: "New password and confirmation don't match." });
    if (pw.next === pw.current) return setPwMsg({ type: "error", text: "New password must be different from your current one." });
    setPwBusy(true);
    try {
      const { data } = await apiClient.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      const bits = ["Password changed successfully."];
      if (data?.other_sessions_logged_out) bits.push("All other devices signed out.");
      if (data?.confirmation_email_sent) bits.push("Confirmation email sent.");
      setPwMsg({ type: "ok", text: bits.join(" ") });
      setPw({ current: "", next: "", confirm: "" });
      setTimeout(() => setPwMsg(null), 6000);
    } catch (err) {
      setPwMsg({ type: "error", text: formatApiError(err, "Could not change password.") });
    } finally {
      setPwBusy(false);
    }
  };

  const u = sms?.usage;

  return (
    <div data-testid="settings-page">
      <WelcomeBanner subtitle="Everything you need to run Newground — your plan, number, credits, profile and security." />

      {/* Plan + Calling: the operating essentials */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Plan & billing */}
        <Card icon={CreditCard} title="Plan & billing" tone="blue" testId="settings-plan-card">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Current plan</div>
              <span className={`inline-flex items-center gap-1.5 text-sm font-bold rounded-full px-3 py-1 border ${pm.cls}`} data-testid="settings-plan-badge">
                <pm.icon className="w-3.5 h-3.5" /> {pm.label}
              </span>
            </div>
            {isPaid && user?.current_period_end && (
              <div className="text-right">
                <div className="text-xs text-slate-400">{user?.cancel_at_period_end ? "Access until" : "Renews"}</div>
                <div className="text-sm font-semibold text-slate-700">{new Date(user.current_period_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
              </div>
            )}
          </div>
          {user?.cancel_at_period_end && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">Your plan is set to cancel at the end of this period.</div>
          )}
          <div className="flex flex-wrap gap-2.5">
            <button data-testid="settings-manage-plan" onClick={() => navigate("/dashboard/plans")}
              className="inline-flex items-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-all shadow-[0_10px_24px_-8px_rgba(29,106,255,0.4)]">
              {plan === "free" ? "Upgrade plan" : "Change plan"} <ArrowRight className="w-4 h-4" />
            </button>
            {isPaid && (
              <button data-testid="settings-manage-billing" onClick={openPortal} disabled={portalBusy}
                className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold rounded-xl px-4 py-2.5 text-sm transition-all disabled:opacity-60">
                {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Manage billing
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Invoices, receipts & payment methods are handled securely by Stripe.</p>
        </Card>

        {/* Calling & texting */}
        <Card icon={Phone} title="Calling & texting" tone="emerald" testId="settings-calling-card">
          {!isPaid ? (
            <div>
              <p className="text-sm text-slate-500 mb-4">Get a local number to call and text leads live from your CRM. Available on Pro and Elite.</p>
              <button data-testid="settings-upgrade-calling" onClick={() => navigate("/dashboard/plans")}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-all">
                <Crown className="w-4 h-4" /> Upgrade to unlock
              </button>
            </div>
          ) : !sms?.active ? (
            <div>
              <p className="text-sm text-slate-500 mb-4">You haven't set up your calling number yet. Pick a local number to start calling & texting leads.</p>
              <button data-testid="settings-choose-number" onClick={() => setShowNumberSetup(true)}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-all">
                <Zap className="w-4 h-4" /> Choose your number
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center"><PhoneForwarded className="w-5 h-5 text-emerald-600" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Your number</div>
                    <div className="font-bold text-slate-900 text-sm tabular-nums" data-testid="settings-number">{sms.smart_number_display}</div>
                  </div>
                </div>
                <button data-testid="settings-copy-number" onClick={copyNumber} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg px-3 py-1.5 transition-all"><Copy className="w-3.5 h-3.5" />{copied ? "Copied!" : "Copy"}</button>
              </div>
              {u && (
                <div data-testid="settings-usage-meter">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-semibold text-slate-600">Credits this month</span>
                    <span className="tabular-nums text-slate-500">{u.used_credits.toLocaleString()} / {u.cap_credits.toLocaleString()}{u.topup_credits > 0 ? ` · +${u.topup_credits.toLocaleString()} top-up` : ""}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full rounded-full ${u.at_limit ? "bg-red-500" : u.near_limit ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, u.pct * 100)}%` }} />
                  </div>
                  <div className="flex items-center justify-between gap-3 mt-2.5">
                    <span className="text-[11px] text-slate-400">{u.remaining_credits.toLocaleString()} credits left · refreshes on the 1st</span>
                    <button data-testid="settings-topup-btn" onClick={() => setShowTopUp(true)} className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-lg px-2.5 py-1 transition-all"><Zap className="w-3.5 h-3.5" /> Top up</button>
                  </div>
                </div>
              )}
              <button data-testid="settings-open-crm" onClick={() => navigate("/dashboard/messages")} className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#1D6AFF] transition-colors">Open CRM to call & text <ArrowRight className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Profile */}
        <form onSubmit={saveProfile} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm lg:col-span-2" data-testid="settings-profile-card">
          <SectionHead icon={User} title="Profile & agency" />
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex flex-col items-center text-center shrink-0">
              <div className="relative mb-2">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#1D6AFF] to-[#0B3FA3] flex items-center justify-center text-white text-2xl font-bold overflow-hidden ring-4 ring-white shadow-lg">
                  {profile.avatar ? <img src={profile.avatar} alt="avatar" className="w-full h-full object-cover" /> : (profile.name || "A").charAt(0).toUpperCase()}
                </div>
                <label className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#1D6AFF] hover:bg-[#1552C6] flex items-center justify-center cursor-pointer shadow-lg" data-testid="settings-photo-upload">
                  <Camera className="w-4 h-4 text-white" />
                  <input type="file" accept="image/*" onChange={onAvatar} className="hidden" />
                </label>
              </div>
              <div className="text-xs text-slate-400 mt-2">Member since {new Date(user?.createdAt || Date.now()).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
            </div>
            <div className="flex-1 grid sm:grid-cols-2 gap-4 content-start">
              <ProfileField testId="settings-name" label="Full name" value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} placeholder="Jane Doe" />
              <ProfileField testId="settings-phone" label="Cell phone" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} placeholder="+1 (415) 555-0100" />
              <ProfileField testId="settings-email" label="Email" value={user?.email || ""} onChange={() => {}} disabled />
              <div className="sm:col-span-2">
                <AgencyPicker value={profile.agency} onChange={(v) => setProfile({ ...profile, agency: v })} />
              </div>
              <div className="sm:col-span-2 flex items-center gap-3 pt-1">
                <button type="submit" data-testid="settings-profile-save" className="inline-flex items-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-6 py-3 transition-all shadow-[0_10px_24px_-8px_rgba(29,106,255,0.4)]">
                  <Save className="w-4 h-4" /> Save changes
                </button>
                {profileSaved && <span className="text-emerald-600 text-sm font-medium" data-testid="settings-profile-saved">Saved!</span>}
              </div>
            </div>
          </div>
        </form>

        {/* Notifications */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <SectionHead icon={Bell} title="Notifications" />
          <Toggle testId="toggle-sms" label="SMS alerts for new leads" desc="Text my cell the moment a lead replies on my Newground number." value={prefs.smsAlerts} onChange={(v) => savePrefs({ ...prefs, smsAlerts: v })} />
          <div className="h-3" />
          <Toggle testId="toggle-digest" label="Weekly email digest" desc="A Monday morning recap of your leads & conversion." value={prefs.emailDigest} onChange={(v) => savePrefs({ ...prefs, emailDigest: v })} />
          {savedToast && (
            <div className="mt-4 text-xs text-emerald-600 flex items-center gap-1.5 font-medium" data-testid="settings-saved">
              <CheckCircle2 className="w-3.5 h-3.5" /> Preferences saved
            </div>
          )}
        </div>

        {/* Integrations */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <SectionHead icon={CheckCircle2} title="Integrations" tone="emerald" />
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5" data-testid="integration-phone">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center border bg-emerald-50 border-emerald-200"><Phone className="w-4 h-4 text-emerald-600" /></div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Calling & texting number</div>
                  <div className="text-xs text-slate-500">{sms?.active ? `Connected · ${sms.smart_number_display}` : isPaid ? "Not set up yet" : "Pro & Elite only"}</div>
                </div>
              </div>
              <button data-testid="integration-phone-action" onClick={() => (isPaid ? (sms?.active ? navigate("/dashboard/messages") : setShowNumberSetup(true)) : navigate("/dashboard/plans"))}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${sms?.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-[#1D6AFF] text-white hover:bg-[#1552C6]"}`}>
                {sms?.active ? "Manage" : isPaid ? "Set up" : "Upgrade"}
              </button>
            </div>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5" data-testid="integration-meta">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center border" style={{ background: "#1877F215", borderColor: "#1877F240" }}><Facebook className="w-4 h-4" style={{ color: "#1877F2" }} /></div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Meta Ads</div>
                  <div className="text-xs text-slate-500">Auto-publish lead campaigns</div>
                </div>
              </div>
              <span className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-100 text-slate-500 border border-slate-200">Coming soon</span>
            </div>
          </div>
        </div>

        {/* Password */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <SectionHead icon={Lock} title="Change password" />
          {user?.auth_method && user.auth_method !== "email" ? (
            <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-4" data-testid="pw-google-note">
              <ShieldCheck className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
              <p className="text-sm text-slate-500 leading-relaxed">You sign in with Google, so your password is managed securely in your Google account. There's nothing to change here.</p>
            </div>
          ) : (
            <form onSubmit={submitPw} className="space-y-4">
              <div className="flex items-start gap-2 bg-[#1D6AFF]/[0.05] border border-[#1D6AFF]/15 rounded-xl px-3.5 py-2.5">
                <ShieldCheck className="w-4 h-4 text-[#1D6AFF] mt-0.5 shrink-0" />
                <p className="text-[11px] text-slate-600 leading-relaxed">For your security we re-check your current password, sign out every other device, and email you a confirmation whenever your password changes.</p>
              </div>
              <PwField testId="pw-current" label="Current password" value={pw.current} onChange={(v) => setPw({ ...pw, current: v })} autoComplete="current-password" />
              <PwField testId="pw-next" label="New password" value={pw.next} onChange={(v) => setPw({ ...pw, next: v })} autoComplete="new-password" />
              <PasswordStrengthMeter password={pw.next} />
              <PwField testId="pw-confirm" label="Confirm new password" value={pw.confirm} onChange={(v) => setPw({ ...pw, confirm: v })} autoComplete="new-password" />
              {pw.next && (
                <ul className="space-y-1.5" data-testid="pw-requirements">
                  <Req ok={pw.next.length >= 8}>At least 8 characters</Req>
                  <Req ok={pw.current && pw.next !== pw.current}>Different from your current password</Req>
                  <Req ok={pw.confirm.length > 0 && pw.next === pw.confirm}>Both new password fields match</Req>
                </ul>
              )}
              {pwMsg && (
                <div className={`text-xs flex items-start gap-1.5 font-medium ${pwMsg.type === "ok" ? "text-emerald-600" : "text-red-500"}`} data-testid="pw-message">
                  {pwMsg.type === "ok" ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                  <span className="leading-snug">{pwMsg.text}</span>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <button type="submit" data-testid="pw-submit" disabled={pwBusy} className="inline-flex items-center gap-1.5 bg-[#1D6AFF] hover:bg-[#1552C6] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl px-5 py-2.5 transition-all">
                  {pwBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {pwBusy ? "Updating…" : "Update password"}
                </button>
                <button type="button" data-testid="pw-forgot" onClick={() => navigate("/forgot-password")} className="text-xs font-semibold text-slate-500 hover:text-[#1D6AFF] transition-colors">Forgot password?</button>
              </div>
            </form>
          )}
        </div>

        {/* Danger zone */}
        <div className="bg-white border border-red-200 rounded-2xl p-6 shadow-sm">
          <SectionHead icon={Trash2} title="Delete account" tone="red" />
          <p className="text-sm text-slate-500 mb-4">Permanently delete your account and all associated data. This action cannot be undone.</p>
          <button data-testid="delete-account-btn" onClick={() => setShowDelete(true)} className="bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl px-5 py-2.5 transition-all">Delete my account</button>
        </div>
      </div>

      {/* Modals */}
      <NumberSetup
        open={showNumberSetup}
        onClose={() => setShowNumberSetup(false)}
        onActivated={onNumberActivated}
        defaultCell={sms?.personal_phone || user?.phone || ""}
        defaultAreaCode={areaCodeOf(sms?.personal_phone || user?.phone)}
      />
      <TopUpModal open={showTopUp} onClose={() => { setShowTopUp(false); loadSms(); }} />

      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="delete-modal">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md" onClick={() => setShowDelete(false)} />
          <div className="relative bg-white border border-slate-200 rounded-3xl p-8 max-w-md w-full shadow-[0_30px_80px_-20px_rgba(15,23,42,0.25)]">
            <h3 className="font-display text-2xl font-bold mb-3 text-slate-900">Delete account?</h3>
            <p className="text-slate-500 mb-6 text-sm leading-relaxed">All your leads, messages, and settings will be permanently removed. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDelete(false)} data-testid="delete-cancel" className="flex-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 rounded-xl py-3 font-semibold transition-all">Keep account</button>
              <button data-testid="delete-confirm" onClick={async () => { await logout(); navigate("/"); }} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl py-3 font-semibold transition-all">Yes, delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TONES = {
  blue: "bg-[#1D6AFF]/10 border-[#1D6AFF]/25 text-[#1D6AFF]",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-600",
  red: "bg-red-50 border-red-200 text-red-500",
};

const SectionHead = ({ icon: Icon, title, tone = "blue" }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${TONES[tone]}`}><Icon className="w-4 h-4" /></div>
    <h2 className="font-display text-xl font-semibold text-slate-900">{title}</h2>
  </div>
);

const Card = ({ icon, title, tone, children, testId }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid={testId}>
    <SectionHead icon={icon} title={title} tone={tone} />
    {children}
  </div>
);

const Toggle = ({ testId, label, desc, value, onChange }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="min-w-0">
      <div className="text-sm font-medium text-slate-900">{label}</div>
      <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
    </div>
    <button data-testid={testId} onClick={() => onChange(!value)} className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${value ? "bg-[#1D6AFF]" : "bg-slate-200"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-sm ${value ? "translate-x-5" : ""}`} />
    </button>
  </div>
);

const ProfileField = ({ testId, label, value, onChange, placeholder, disabled }) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">{label}</label>
    <input data-testid={testId} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none text-slate-900 placeholder:text-slate-400 rounded-xl px-4 py-3 text-sm transition-all disabled:opacity-60" />
  </div>
);

const PwField = ({ testId, label, value, onChange, autoComplete }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">{label}</label>
      <div className="relative">
        <input data-testid={testId} type={show ? "text" : "password"} value={value} autoComplete={autoComplete} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none text-slate-900 rounded-xl pl-4 pr-11 py-2.5 text-sm transition-all" />
        <button type="button" data-testid={`${testId}-toggle`} onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const Req = ({ ok, children }) => (
  <li className={`flex items-center gap-2 text-xs transition-colors ${ok ? "text-emerald-600" : "text-slate-400"}`}>
    <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 border ${ok ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
      <Check className={`w-2.5 h-2.5 ${ok ? "opacity-100" : "opacity-30"}`} />
    </span>
    {children}
  </li>
);

const US_AGENCIES = [
  "Keller Williams", "RE/MAX", "Coldwell Banker", "Century 21", "eXp Realty",
  "Compass", "Berkshire Hathaway HomeServices", "Sotheby's International Realty",
  "Better Homes and Gardens", "Realty ONE Group", "Weichert, Realtors",
  "Howard Hanna", "Redfin", "@properties", "Douglas Elliman",
];

const AgencyPicker = ({ value, onChange }) => {
  const trackRef = useRef(null);
  const [other, setOther] = useState(() => Boolean(value) && !US_AGENCIES.includes(value));

  const scroll = (dir) => trackRef.current?.scrollBy({ left: dir * 260, behavior: "smooth" });
  const pick = (name) => { setOther(false); onChange(name); };
  const pickOther = () => { setOther(true); onChange(US_AGENCIES.includes(value) ? "" : value); };

  return (
    <div data-testid="settings-agency-picker">
      <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Agency / brokerage</label>
      <div className="relative">
        <button type="button" data-testid="agency-scroll-left" onClick={() => scroll(-1)} aria-label="Scroll left"
          className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-slate-200 shadow-sm items-center justify-center text-slate-500 hover:text-[#1D6AFF] hover:border-[#1D6AFF]/40 transition-all">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div ref={trackRef} className="flex gap-2 overflow-x-auto scroll-smooth sm:px-10 pb-1 agency-track">
          {US_AGENCIES.map((name) => {
            const active = !other && value === name;
            return (
              <button type="button" key={name} data-testid={`agency-chip-${name}`} onClick={() => pick(name)}
                className={`shrink-0 inline-flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-medium border transition-all ${
                  active ? "bg-[#1D6AFF] border-[#1D6AFF] text-white shadow-[0_8px_20px_-8px_rgba(29,106,255,0.5)]" : "bg-slate-50 border-slate-200 text-slate-700 hover:border-[#1D6AFF]/40 hover:bg-white"
                }`}>
                <Building2 className={`w-4 h-4 ${active ? "text-white" : "text-slate-400"}`} />{name}
              </button>
            );
          })}
          <button type="button" data-testid="agency-chip-other" onClick={pickOther}
            className={`shrink-0 inline-flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-medium border transition-all ${
              other ? "bg-[#1D6AFF] border-[#1D6AFF] text-white shadow-[0_8px_20px_-8px_rgba(29,106,255,0.5)]" : "bg-slate-50 border-slate-200 text-slate-700 hover:border-[#1D6AFF]/40 hover:bg-white"
            }`}>
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[11px] font-bold ${other ? "bg-white/20" : "bg-slate-200 text-slate-500"}`}>+</span>Other
          </button>
        </div>
        <button type="button" data-testid="agency-scroll-right" onClick={() => scroll(1)} aria-label="Scroll right"
          className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-slate-200 shadow-sm items-center justify-center text-slate-500 hover:text-[#1D6AFF] hover:border-[#1D6AFF]/40 transition-all">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      {other && (
        <input data-testid="agency-other-input" value={value} autoFocus onChange={(e) => onChange(e.target.value)} placeholder="Type your agency or brokerage name"
          className="mt-3 w-full bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none text-slate-900 placeholder:text-slate-400 rounded-xl px-4 py-3 text-sm transition-all" />
      )}
      <style>{`.agency-track{scrollbar-width:none;-ms-overflow-style:none}.agency-track::-webkit-scrollbar{display:none}`}</style>
    </div>
  );
};
